// my-machines.js — Machines → "My machines" panel: per-user list of
// devices the signed-in person has approved into their account, with
// active/cap counter, server-paginated list, rename, revoke, install-
// command flow for a new machine, and an "at-cap" upgrade nudge.
//
// renderMyMachines is exported; bootstrap.js re-exposes it on
// AC_LEGACY so pages/settings.js can call it on mount. Module init
// wires the document-level delegated click handler (Add / Copy /
// Close / Advanced toggle / Revoke / Rename).
//
// The "active machine" cap is enforced by the coordinator based on
// the team's tier. The cap-reached nudge uses an inline onclick="..."
// string that calls renderBillingStatus(); that name resolves through
// window because billing.js's wireListeners exposes it on window.
//
// Bridge dependencies: getSupabase() lazily — only Supabase-authed
// users get the panel (CLI-only team-token users hide it because the
// /v1/me/machines endpoint is supabase_user_id-scoped).
//
// Hoisted from bootstrap.js on 2026-05-13 (Phase K Tier 2k).
import { escapeHTML, ago } from "./util.js";
import { showToast, showConfirm, showPrompt } from "./ui.js";
import { api, apiAsUser, apiWithMessage } from "./api.js";

function getSupabase() {
  const legacy = window.AC_LEGACY;
  return legacy && typeof legacy.getSupabase === "function"
    ? legacy.getSupabase()
    : null;
}

export async function renderMyMachines() {
  const panel = document.getElementById('my-machines-panel');
  const list = document.getElementById('my-machines-list');
  if (!panel || !list) return;

  // Only show to Supabase-authed users (requires supabase_user_id on the token).
  let hasUserSession = false;
  try {
    const supabase = getSupabase();
    if (supabase) {
      const { data: { session } } = await supabase.auth.getSession();
      hasUserSession = !!session;
    }
  } catch {}
  if (!hasUserSession) { panel.style.display = 'none'; return; }

  panel.style.display = '';

  try {
    // Server-side pagination via ?limit=&offset=. Page state lives
    // on window so prev/next clicks re-render the panel without
    // refetching the entire user's machine list. Page size 5 —
    // small enough not to dominate settings, large enough that most
    // users see everything on page 1.
    window._myMachinesPage = window._myMachinesPage || 1;
    const PAGE_SIZE = 5;
    const page = window._myMachinesPage;
    const offset = (page - 1) * PAGE_SIZE;
    const data = await api('/v1/me/machines?limit=' + PAGE_SIZE + '&offset=' + offset);
    const machines = data.machines || [];
    const activeCount = data.active_count || 0;
    const activeCap = data.active_cap || 1;
    const totalCount = data.total_count || machines.length;
    const tier = data.tier || 'solo';

    // If the user navigated to a page that no longer has machines
    // (e.g., they revoked some), fall back to page 1 and re-render.
    const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
    if (page > totalPages) {
      window._myMachinesPage = 1;
      return renderMyMachines();
    }

    // Header strip with active/cap readout
    const header = document.getElementById('my-machines-panel').querySelector('.panel-header');
    const existingCounter = header.querySelector('.my-machines-counter');
    if (existingCounter) existingCounter.remove();
    // Comp accounts are uncapped — never show "6 of 5" in warning color.
    const capUnlimited = data.active_cap_unlimited === true;
    const capColor = (!capUnlimited && activeCount >= activeCap) ? 'var(--warning)' : 'var(--success)';
    const counter = document.createElement('span');
    counter.className = 'my-machines-counter';
    counter.style.cssText = 'font-size: 11px; padding: 3px 10px; border-radius: 999px; font-weight: 600; letter-spacing: 0.05em; background: rgba(52,211,153,0.1); color: ' + capColor + '; margin-right: var(--s2);';
    counter.textContent = capUnlimited
      ? activeCount + ' machine' + (activeCount === 1 ? '' : 's') + ' in use (unlimited)'
      : activeCount + ' of ' + activeCap + ' machines in use';
    header.insertBefore(counter, header.querySelector('button'));

    if (totalCount === 0) {
      list.innerHTML = '<div style="padding: var(--s5); text-align: center; color: var(--text-tertiary); font-size: 13px; line-height: 1.6;">No machines connected yet.<br/>Click <strong>Install on a new machine</strong> above to get a one-line install command.</div>';
      return;
    }

    list.innerHTML = machines.map((m) => {
      // Plain-English status. "Running" = the daemon checked in recently
      // (is_running, from last_used_at — covers pushing AND pulling daemons),
      // which is the honest "is this machine working right now" signal.
      // is_active is the separate concurrency-slot flag the plan cap counts;
      // we surface it as "Coordinating now" only as a stronger shade of running,
      // never as the bare word "active" (which read as jargon).
      let status, statusColor;
      const isRunning = m.is_running !== undefined ? m.is_running : (m.is_active || m.is_online);
      if (m.cooldown_seconds_remaining > 0) {
        status = 'Paused — back in ' + m.cooldown_seconds_remaining + 's'; statusColor = 'var(--warning)';
      }
      else if (m.was_bumped && !isRunning) { status = 'Paused — waiting for a free spot'; statusColor = 'var(--warning)'; }
      else if (isRunning) { status = 'Running'; statusColor = 'var(--success)'; }
      else if (m.last_seen) { status = 'Not running'; statusColor = 'var(--text-tertiary)'; }
      else { status = 'Never connected'; statusColor = 'var(--text-tertiary)'; }

      const dotColor = isRunning ? 'var(--success)' : 'var(--text-tertiary)';
      const dotShadow = isRunning ? 'box-shadow:0 0 8px rgba(52,211,153,0.6);' : '';
      const last = m.last_seen ? ago(m.last_seen) : 'never';
      const ip = m.last_used_ip ? ' &middot; ' + escapeHTML(m.last_used_ip) : '';
      // Label fallback: the worker returns "Unnamed machine" when
      // tokens.label is null, but we can do better with the laptop_id
      // (prettified to drop the trailing entropy chars) as a graceful
      // placeholder. Pre-fix every freshly-installed machine showed
      // "Unnamed machine" until the user discovered `agc device name`
      // — now we show a guessed hostname and surface the Rename
      // button right next to it.
      const isUnnamed = !m.label || m.label === 'Unnamed machine';
      const displayName = isUnnamed
        ? (m.laptop_id || 'Machine').replace(/^device-/, '').replace(/^laptop_/, '')
        : m.label;
      const unnamedHint = isUnnamed
        ? ' <span style="font-size: 11px; color: var(--text-tertiary); font-style: italic; font-weight: 400;">(not yet named &mdash; click Rename to set a friendly label)</span>'
        : '';
      return `<div style="padding: var(--s3) var(--s5); border-bottom: 1px solid var(--border); display: flex; align-items: center; gap: var(--s3);">
        <div style="min-width:0;flex:1;">
          <div style="font-weight: 600; font-size: 14px; color: var(--text);">
            <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${dotColor};${dotShadow}margin-right:8px;"></span>${escapeHTML(displayName)}${unnamedHint}
            <span style="font-size: 10px; font-weight: 600; padding: 1px 8px; border-radius: 999px; background: ${m.is_active ? 'rgba(52,211,153,0.12)' : 'var(--bg-elevated)'}; color: ${statusColor}; margin-left: 8px; text-transform: uppercase; letter-spacing: 0.05em;">${escapeHTML(status)}</span>
          </div>
          <div style="font-size: 11px; color: var(--text-tertiary); margin-top: 2px; margin-left: 16px; font-family: var(--font-mono);">${escapeHTML(m.laptop_id)}</div>
          <div style="font-size: 12px; color: var(--text-tertiary); margin-top: 2px; margin-left: 16px;">last seen ${last}${ip}</div>
        </div>
        <div style="display: flex; gap: var(--s2); flex-shrink: 0;">
          <button class="btn btn-sm" data-page-link="machine-settings" style="font-size: 11px; padding: 3px 10px;">Settings</button>
          <button class="btn btn-sm" data-my-machine-rename="${escapeHTML(m.id)}" data-current-label="${escapeHTML(isUnnamed ? '' : m.label)}" style="font-size: 11px; padding: 3px 10px;">Rename</button>
          <button class="btn btn-sm" data-my-machine-revoke="${escapeHTML(m.id)}" style="font-size: 11px; padding: 3px 10px; color: var(--danger); border-color: var(--danger);">Revoke</button>
        </div>
      </div>`;
    }).join('');

    // Pager footer — only when there's actually more than one page.
    // Uses server-reported totalCount (not the page slice) so the
    // counts stay accurate as the user paginates.
    if (totalPages > 1) {
      const startIdx = offset;
      const endIdx = Math.min(offset + machines.length, totalCount);
      const pager = document.createElement('div');
      pager.style.cssText = 'padding: var(--s3) var(--s5); display: flex; align-items: center; justify-content: space-between; font-size: 12px; color: var(--text-tertiary); border-top: 1px solid var(--border);';
      pager.innerHTML =
        '<span>Showing <strong>' + (startIdx + 1) + '–' + endIdx + '</strong> of ' + totalCount + ' machines</span>' +
        '<span style="display: flex; gap: 6px;">' +
          '<button class="btn btn-sm" id="my-machines-prev" ' + (page <= 1 ? 'disabled' : '') + ' style="font-size: 11px; padding: 3px 10px;">Prev</button>' +
          '<span style="padding: 4px 10px;">Page ' + page + ' of ' + totalPages + '</span>' +
          '<button class="btn btn-sm" id="my-machines-next" ' + (page >= totalPages ? 'disabled' : '') + ' style="font-size: 11px; padding: 3px 10px;">Next</button>' +
        '</span>';
      list.appendChild(pager);
      // Wire pager buttons. Re-render on click; state lives on window
      // so the new render picks up the new page number.
      const prev = document.getElementById('my-machines-prev');
      const next = document.getElementById('my-machines-next');
      if (prev && page > 1) prev.onclick = () => { window._myMachinesPage = page - 1; renderMyMachines(); };
      if (next && page < totalPages) next.onclick = () => { window._myMachinesPage = page + 1; renderMyMachines(); };
    }

    // Cap + upgrade nudge if at cap. Sits below the pager.
    // 2026-06-23: Billing is its own tab now. The inline onclick navigates to
    // the Billing tab (window.navigate is exposed by router.js) — which mounts
    // the billing page + renders it — then reveals the plan picker. Previously
    // it called renderBillingStatus() + un-hid #billing-plan-picker in place,
    // but that element now lives on the (hidden) billing page, so revealing it
    // from the Machines page showed nothing.
    if (activeCount >= activeCap && tier !== 'enterprise') {
      const footer = document.createElement('div');
      footer.style.cssText = 'padding: var(--s3) var(--s5); background: rgba(251,191,36,0.05); font-size: 12px; color: var(--text-secondary); line-height: 1.5;';
      footer.innerHTML = 'Your plan covers <strong>' + activeCap + ' machine' + (activeCap === 1 ? '' : 's') + '</strong> at once. Connecting another will disconnect one of these. <a href="#" onclick="window.navigate(\'billing\'); setTimeout(function(){ var el = document.getElementById(\'billing-plan-picker\'); if (el) el.style.display=\'\'; window.scrollTo(0, document.body.scrollHeight); }, 60); return false;" style="color: var(--accent);">Upgrade</a> to use more.';
      list.appendChild(footer);
    }
  } catch (e) {
    // Transient coordinator/D1 hiccups (e.g. a 500 during a Worker
    // rollout) shouldn't strand the user on a bare error line with no
    // way out. Show the failure plus a Retry that re-renders — most
    // of these clear on the next request. The button id is wired by
    // the delegated click handler in wireListeners().
    list.innerHTML =
      '<div style="padding: var(--s4); font-size: 13px; display: flex; align-items: center; gap: var(--s3); flex-wrap: wrap;">' +
        '<span style="color: var(--danger);">Couldn’t load your machines: ' + escapeHTML(e.message || 'unknown') + '</span>' +
        '<button class="btn btn-sm" id="my-machines-retry" style="font-size: 11px; padding: 3px 10px;">Retry</button>' +
      '</div>';
  }
}

// renderLocalMachinePanel — fills the daemon-only "This machine"
// panel on the Machines page. The cloud-only "My machines" panel is
// CSS-hidden in local-daemon mode (it needs a Supabase session to
// scope per-user machines), which used to leave the Machines page as
// just a title + empty body. This panel gives the local daemon a real
// view: who this machine reports as + its team binding + connect-more
// instructions. Best-effort: every line is independently optional, so
// a missing field just drops that row rather than erroring.
//
// Reads identity from the shared whoami cache (window.AC_LEGACY
// bridge, same source as the sidebar badge) and the team binding from
// the daemon's loopback /v1/teams/info. Neither is required for the
// panel to be useful — the install commands are static.
export async function renderLocalMachinePanel() {
  // Only do work in local-daemon mode. In cloud modes the panel is
  // .daemon-only (CSS-hidden but still in the DOM), so guarding on
  // element presence alone wouldn't stop us from firing a needless
  // /v1/teams/info and writing into hidden nodes. Bail on deploy mode.
  const mode = window.AC_CONFIG && window.AC_CONFIG.DEPLOY_MODE;
  if (mode && mode !== 'local-daemon') return;

  const panel = document.getElementById('local-machine-panel');
  if (!panel) return;

  const nameEl = document.getElementById('local-machine-name');
  const idEl = document.getElementById('local-machine-id');
  const teamEl = document.getElementById('local-machine-team');

  // Identity (display name) from the cached whoami the sidebar already
  // fetched. getWhoami() lives on the AC_LEGACY bridge.
  try {
    const legacy = window.AC_LEGACY;
    const who = legacy && typeof legacy.getWhoami === 'function' ? legacy.getWhoami() : null;
    if (who && nameEl) {
      const display = who.email || who.identity || 'This machine';
      nameEl.textContent = display;
    }
  } catch {}

  // Team binding — the daemon's solo /v1/teams/info returns
  // { team_name | display_name, laptop_id? }. Loopback, no bearer.
  try {
    const info = await api('/v1/teams/info');
    if (info) {
      const teamName = info.team_name || info.display_name || info.name;
      if (teamName && teamEl) teamEl.textContent = 'Team: ' + teamName;
      const lid = info.laptop_id || info.machine_id;
      if (lid && idEl) idEl.textContent = lid;
    }
  } catch {
    // Solo / unbound daemon — /v1/teams/info may 404 or return
    // empty. That's fine; the panel still shows the install flow.
  }
}

// ---- module init: delegated click handlers ----

function wireListeners() {
  document.addEventListener('click', async (e) => {
    // Retry after a failed /v1/me/machines load (transient 500, etc.).
    if (e.target.id === 'my-machines-retry') {
      renderMyMachines();
      return;
    }
    if (e.target.id === 'my-machines-add-btn') {
      const label = await showPrompt(
        'Install on a new machine',
        'Give this machine a name so you can recognize it later.',
        { placeholder: 'e.g., work MacBook', actionLabel: 'Create install command' }
      );
      if (!label) return;
      try {
        const res = await apiWithMessage('/v1/me/install-token', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ label }),
        });
        document.getElementById('my-machines-reveal').style.display = '';
        document.getElementById('my-machines-install-cmd').textContent = res.install_command || '';
        renderMyMachines();
      } catch (err) {
        showToast('error', 'Failed to create install command: ' + (err.message || 'unknown'));
      }
      return;
    }
    if (e.target.id === 'my-machines-install-copy') {
      const cmd = document.getElementById('my-machines-install-cmd').textContent;
      try {
        await navigator.clipboard.writeText(cmd);
        e.target.textContent = 'Copied!';
        setTimeout(() => { e.target.textContent = 'Copy'; }, 1500);
      } catch {}
      return;
    }
    if (e.target.id === 'my-machines-install-close') {
      document.getElementById('my-machines-reveal').style.display = 'none';
      document.getElementById('my-machines-install-cmd').textContent = '';
      return;
    }
    // Advanced section toggle — reveals the token-embedded install
    // flow. Uses closest() so the click is still caught when the user
    // hits the caret (nested inside the button) rather than the text.
    const advToggle = e.target.closest && e.target.closest('#my-machines-advanced-toggle');
    if (advToggle) {
      const body = document.getElementById('my-machines-advanced-body');
      const caret = document.getElementById('my-machines-advanced-caret');
      const isOpen = body.style.display !== 'none';
      body.style.display = isOpen ? 'none' : '';
      if (caret) caret.style.transform = isOpen ? '' : 'rotate(90deg)';
      return;
    }
    // The per-row "Settings" button now navigates to the dedicated Settings
    // page (data-page-link="machine-settings", handled by the global nav
    // delegate) instead of opening a modal — so there's a single place to edit
    // per-machine settings and no duplicate-id collision between a modal form
    // and the page form.
    if (e.target.dataset && e.target.dataset.myMachineRevoke) {
      const id = e.target.dataset.myMachineRevoke;
      const ok = await showConfirm(
        'Revoke this machine?',
        'Its daemon will stop working and you’ll need to reinstall to reconnect.',
        'Revoke'
      );
      if (!ok) return;
      try {
        await api('/v1/me/machines/' + id, { method: 'DELETE' });
        renderMyMachines();
        showToast('success', 'Machine revoked.');
      } catch (err) {
        showToast('error', 'Revoke failed: ' + (err.message || 'unknown'));
      }
      return;
    }
    // Rename machine — inline prompt; persists to tokens.label via
    // PATCH /v1/me/machines/<id>. Surfaces in the My machines panel
    // and any other view that reads the machine label.
    if (e.target.dataset && e.target.dataset.myMachineRename) {
      const id = e.target.dataset.myMachineRename;
      const current = e.target.dataset.currentLabel || '';
      const label = await showPrompt(
        'Rename machine',
        'Give this machine a label so you can recognize it. 1–64 characters.',
        { placeholder: 'e.g. work MacBook, home Linux box, CI runner', initialValue: current, actionLabel: 'Save' }
      );
      if (label === null) return; // user cancelled
      const trimmed = (label || '').trim();
      if (!trimmed) {
        showToast('error', 'Label cannot be empty.');
        return;
      }
      try {
        // apiAsUser (not api) so the server's structured error
        // message surfaces in toasts. api() throws "<path>: <status>"
        // which is opaque; apiAsUser parses {error, message} and
        // throws the human-readable message.
        await apiAsUser('/v1/me/machines/' + id, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ label: trimmed }),
        });
        renderMyMachines();
        showToast('success', 'Machine renamed.');
      } catch (err) {
        showToast('error', 'Rename failed: ' + (err.message || 'unknown'));
      }
      return;
    }
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", wireListeners);
} else {
  wireListeners();
}
